/**
 * useProjects — TanStack Query hooks for Projects.
 * Spec 59 Phase D.
 *
 * Every request and every cache key is the generated one (`xOptions` / `xQueryKey` / `xMutation`).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  addProjectMemberMutation,
  archiveProjectMutation,
  closeProjectMutation,
  createProjectChangeMutation,
  createProjectMutation,
  createProjectThreadMutation,
  getProjectChangeOptions,
  getProjectCommandCenterOptions,
  getProjectCommandCenterQueryKey,
  getProjectLogOptions,
  getProjectOptions,
  getProjectQueryKey,
  getProjectSummaryOptions,
  getProjectSummaryQueryKey,
  listProjectChangesOptions,
  listProjectMembersOptions,
  listProjectMembersQueryKey,
  listProjectWorkModeTemplatesOptions,
  listProjectWorkModeTemplatesQueryKey,
  listProjectsOptions,
  listProjectsQueryKey,
  reactivateProjectMutation,
  removeProjectMemberMutation,
  replaceProjectWorkModeTemplatesMutation,
  updateProjectMemberRoleMutation,
  updateProjectMutation,
} from '../generated/api/@tanstack/react-query.gen.js';
import { adaptMutation } from './adapt-mutation.js';
import type {
  Project,
  ProjectMember,
  ProjectSummary,
  ProjectThread,
  ProjectChange,
  ProjectChangeReadinessCheck,
  ProjectChangeRevision,
  ProjectChangeGate,
  ProjectWorkModeTemplate,
  ProjectLifecyclePhase,
  AttentionQueueItem,
  AgentHealth,
  ProjectCounts,
  ProjectActivityItem,
  ProjectThreadResource,
  ProjectThreadOwner,
  ProjectCommandCenter,
  ProjectChangeView,
  ProjectChangesResult,
  ProjectLogCommit,
  ProjectLogLane,
  ProjectLogDecisionCard,
  ProjectLogResult,
  ListProjectsData,
  ProjectList,
  ListProjectChangesData,
  GetProjectLogData,
  CreateProjectData,
  UpdateProjectData,
  CloseProjectData,
  ProjectMemberAddRequest,
  ProjectMemberUpdateRoleRequest,
  ProjectThreadCreate,
  ProjectChangeCreate,
  ProjectWorkModeTemplateInput,
} from '../generated/api/types.gen.js';
export type {
  Project,
  ProjectMember,
  ProjectSummary,
  ProjectThread,
  ProjectChange,
  ProjectChangeReadinessCheck,
  ProjectChangeRevision,
  ProjectChangeGate,
  ProjectWorkModeTemplate,
  ProjectLifecyclePhase,
  AttentionQueueItem,
  AgentHealth,
  ProjectCounts,
  ProjectActivityItem,
  ProjectThreadResource,
  ProjectThreadOwner,
  ProjectCommandCenter,
  ProjectChangeView,
  ProjectChangesResult,
  ProjectLogCommit,
  ProjectLogLane,
  ProjectLogDecisionCard,
  ProjectLogResult,
};

// ─── Types derived from the generated contract ───────────────────────────────

export type ProjectListResponse = ProjectList;
export type ProjectFilters = NonNullable<ListProjectsData['query']>;
export type ProjectStatus = NonNullable<ProjectFilters['status']>;
export type MemberType = ProjectMemberAddRequest['memberType'];
export type ProjectLifecyclePhaseStatus = ProjectLifecyclePhase['status'];
export type AttentionKind = AttentionQueueItem['kind'];
export type AttentionSeverity = AttentionQueueItem['severity'];
export type ProjectChangesFilters = NonNullable<ListProjectChangesData['query']>;
export type ProjectChangeDisplayStatus = NonNullable<ProjectChangesFilters['status']>;
export type ProjectLogFilters = NonNullable<GetProjectLogData['query']>;

// ─── Queries ──────────────────────────────────────────────────────────────────

export function useProjectsList(filters: ProjectFilters = {}) {
  const query = useQuery(listProjectsOptions({ query: filters }));
  if (query.isError) console.error('useProjectsList: failed to load projects', query.error);
  return query;
}

export function useProject(id: number | string | undefined) {
  return useQuery({ ...getProjectOptions({ path: { id: String(id) } }), enabled: !!id });
}

export function useProjectSummary(id: number | string | undefined) {
  return useQuery({ ...getProjectSummaryOptions({ path: { id: String(id) } }), enabled: !!id });
}

export function useProjectCommandCenter(id: number | string | undefined) {
  return useQuery({ ...getProjectCommandCenterOptions({ path: { id: String(id) } }), enabled: !!id });
}

export function useProjectWorkModeTemplates(id: number | string | undefined) {
  return useQuery({ ...listProjectWorkModeTemplatesOptions({ path: { id: String(id) } }), enabled: !!id });
}

export function useProjectMembers(projectId: number | string | undefined) {
  return useQuery({ ...listProjectMembersOptions({ path: { id: String(projectId) } }), enabled: !!projectId });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adaptMutation(createProjectMutation(), (body: NonNullable<CreateProjectData['body']>) => ({ body })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listProjectsQueryKey() });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adaptMutation(
      updateProjectMutation(),
      ({ id, patch }: { id: number | string; patch: NonNullable<UpdateProjectData['body']> }) => ({
        path: { id: String(id) },
        body: patch,
      }),
    ),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: getProjectQueryKey({ path: { id: String(id) } }) });
      queryClient.invalidateQueries({ queryKey: listProjectsQueryKey() });
    },
  });
}

export function useReplaceProjectWorkModeTemplates() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adaptMutation(
      replaceProjectWorkModeTemplatesMutation(),
      ({ id, templates }: { id: number | string; templates: ProjectWorkModeTemplateInput[] }) => ({
        path: { id: String(id) },
        body: { templates },
      }),
    ),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: listProjectWorkModeTemplatesQueryKey({ path: { id: String(id) } }),
      });
      queryClient.invalidateQueries({ queryKey: getProjectCommandCenterQueryKey({ path: { id: String(id) } }) });
    },
  });
}

export function useArchiveProject() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adaptMutation(archiveProjectMutation(), (id: number | string) => ({ path: { id: String(id) } })),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: getProjectQueryKey({ path: { id: String(id) } }) });
      queryClient.invalidateQueries({ queryKey: listProjectsQueryKey() });
    },
  });
}

export function useReactivateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adaptMutation(reactivateProjectMutation(), (id: number | string) => ({ path: { id: String(id) } })),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: getProjectQueryKey({ path: { id: String(id) } }) });
      queryClient.invalidateQueries({ queryKey: listProjectsQueryKey() });
    },
  });
}

export function useCloseProject() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adaptMutation(
      closeProjectMutation(),
      ({ id, reason }: { id: number | string; reason?: NonNullable<CloseProjectData['body']>['reason'] }) => ({
        path: { id: String(id) },
        body: { reason },
      }),
    ),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: getProjectQueryKey({ path: { id: String(id) } }) });
      queryClient.invalidateQueries({ queryKey: listProjectsQueryKey() });
    },
  });
}

export function useAddProjectMember() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adaptMutation(
      addProjectMemberMutation(),
      ({ projectId, ...body }: { projectId: number | string } & ProjectMemberAddRequest) => ({
        path: { id: String(projectId) },
        body,
      }),
    ),
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: listProjectMembersQueryKey({ path: { id: String(projectId) } }) });
      queryClient.invalidateQueries({ queryKey: getProjectSummaryQueryKey({ path: { id: String(projectId) } }) });
    },
  });
}

export function useRemoveProjectMember() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adaptMutation(
      removeProjectMemberMutation(),
      ({
        projectId,
        memberId,
        memberType,
      }: {
        projectId: number | string;
        memberId: number;
        memberType: MemberType;
      }) => ({
        path: { id: String(projectId), memberId: String(memberId) },
        query: { memberType },
      }),
    ),
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: listProjectMembersQueryKey({ path: { id: String(projectId) } }) });
      queryClient.invalidateQueries({ queryKey: getProjectSummaryQueryKey({ path: { id: String(projectId) } }) });
    },
  });
}

export function useUpdateProjectMemberRole() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adaptMutation(
      updateProjectMemberRoleMutation(),
      ({
        projectId,
        memberId,
        memberType,
        role,
      }: {
        projectId: number | string;
        memberId: number;
        memberType: MemberType;
        role: ProjectMemberUpdateRoleRequest['role'];
      }) => ({
        path: { id: String(projectId), memberId: String(memberId) },
        query: { memberType },
        body: { role },
      }),
    ),
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: listProjectMembersQueryKey({ path: { id: String(projectId) } }) });
    },
  });
}

export function useCreateProjectThread() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adaptMutation(
      createProjectThreadMutation(),
      ({ projectId, body }: { projectId: number | string; body: ProjectThreadCreate }) => ({
        path: { id: String(projectId) },
        body,
      }),
    ),
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: getProjectCommandCenterQueryKey({ path: { id: String(projectId) } }) });
      queryClient.invalidateQueries({ queryKey: getProjectSummaryQueryKey({ path: { id: String(projectId) } }) });
    },
  });
}

export function useCreateProjectChange() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adaptMutation(
      createProjectChangeMutation(),
      ({ projectId, body }: { projectId: number | string; body: ProjectChangeCreate }) => ({
        path: { id: String(projectId) },
        body,
      }),
    ),
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: getProjectCommandCenterQueryKey({ path: { id: String(projectId) } }) });
      queryClient.invalidateQueries({ queryKey: getProjectSummaryQueryKey({ path: { id: String(projectId) } }) });
    },
  });
}

// ─── Changes with stats ───────────────────────────────────────────────────────

export function useProjectChangesWithStats(id: number | string | undefined, filters?: ProjectChangesFilters) {
  return useQuery({
    ...listProjectChangesOptions({ path: { id: String(id) }, query: filters }),
    enabled: !!id,
  });
}

const CHANGE_DETAIL_EXPAND = 'gates,revisions,resources,activity,thread';

/** Change Detail — full hydration for the Change Detail page (all expansions). */
export function useProjectChange(projectId: number | string | undefined, changeId: number | string | undefined) {
  return useQuery({
    ...getProjectChangeOptions({
      path: { id: String(projectId), changeId: String(changeId) },
      query: { expand: CHANGE_DETAIL_EXPAND },
    }),
    enabled: !!projectId && !!changeId,
  });
}

// ─── Project Log ──────────────────────────────────────────────────────────────

export function useProjectLog(id: number | string | undefined, filters?: ProjectLogFilters) {
  return useQuery({ ...getProjectLogOptions({ path: { id: String(id) }, query: filters }), enabled: !!id });
}
